<template>

  <section
    v-if="slideCount > 0"
    id="section-home"
    class="hero-section"
    @mouseenter="stopSlider"
    @mouseleave="startSlider"
  >

    <div class="hero-section__slides" aria-hidden="true">

      <div

        ref="track"

        class="hero-section__track"

        :class="{ 'hero-section__track--instant': noTransition }"

        :style="trackStyle"

        @transitionend="onTrackTransitionEnd"

      >

        <div

          v-for="(src, index) in trackSlides"

          :key="`hero-${slideKey}-${index}`"

          class="hero-section__slide"

        >

          <img :src="src" alt="" loading="eager" decoding="async">

        </div>

      </div>

    </div>

  </section>

</template>

<script>

import { mapState } from 'vuex'
import { resolveHeroSlides } from '@/utils/heroSlides'

const SLIDE_INTERVAL_MS = 3200

const SLIDE_DURATION_MS = 520

export default {

  name: 'BlackGoldHero',

  data () {
    return {
      currentIndex: 0,
      noTransition: false,
      isSnapping: false,
      slideTimer: null,
      snapFallbackTimer: null,
      slideKey: 0
    }
  },

  computed: {

    ...mapState({
      bannerList: state => state.bannerList
    }),

    heroSlides () {
      return resolveHeroSlides(this.bannerList, this.$i18n.locale)
    },

    slideCount () {
      return this.heroSlides.length
    },

    trackSlides () {
      return [...this.heroSlides, ...this.heroSlides]
    },

    trackStyle () {
      return {

        transform: `translate3d(-${this.currentIndex * 100}%, 0, 0)`,

        transitionDuration: `${SLIDE_DURATION_MS}ms`

      }
    }

  },

  watch: {
    '$i18n.locale' () {
      this.resetSlider()
    },
    heroSlides (slides, prev) {
      const nextLen = Array.isArray(slides) ? slides.length : 0
      const prevLen = Array.isArray(prev) ? prev.length : 0
      if (nextLen !== prevLen) {
        this.resetSlider()
      }
    }
  },

  mounted () {
    this.startSlider()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('pageshow', this.onPageShow)
  },

  beforeDestroy () {
    this.stopSlider()
    this.clearSnapFallback()
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('pageshow', this.onPageShow)
  },

  methods: {
    onVisibilityChange () {
      if (document.visibilityState === 'hidden') {
        this.stopSlider()
        return
      }
      this.recoverSlider()
    },
    onPageShow (event) {
      if (event && event.persisted) {
        this.recoverSlider()
      }
    },
    recoverSlider () {
      if (this.slideCount === 0) {
        this.stopSlider()
        return
      }
      if (this.isSnapping) {
        this.isSnapping = false
        this.noTransition = false
      }
      if (this.currentIndex >= this.slideCount) {
        this.currentIndex = 0
        this.noTransition = true
        this.$nextTick(() => {
          const track = this.$refs.track
          if (track) void track.offsetHeight
          window.requestAnimationFrame(() => {
            this.noTransition = false
            this.startSlider()
          })
        })
        return
      }
      this.startSlider()
    },
    clearSnapFallback () {
      if (this.snapFallbackTimer) {
        window.clearTimeout(this.snapFallbackTimer)
        this.snapFallbackTimer = null
      }
    },
    scheduleSnapFallback () {
      this.clearSnapFallback()
      this.snapFallbackTimer = window.setTimeout(() => {
        if (this.currentIndex === this.slideCount) {
          this.snapToStart()
        }
      }, SLIDE_DURATION_MS + 100)
    },
    resetSlider () {
      this.stopSlider()
      this.clearSnapFallback()
      this.currentIndex = 0
      this.isSnapping = false
      this.noTransition = true
      this.slideKey += 1
      this.$nextTick(() => {
        const track = this.$refs.track
        if (track) {
          void track.offsetHeight
        }
        window.requestAnimationFrame(() => {
          this.noTransition = false
          this.startSlider()
        })
      })
    },
    startSlider () {
      this.stopSlider()
      if (this.slideCount === 0) return
      this.slideTimer = window.setInterval(this.nextSlide, SLIDE_INTERVAL_MS)
    },
    stopSlider () {
      if (this.slideTimer) {
        window.clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },
    nextSlide () {
      if (this.isSnapping || this.noTransition || this.slideCount === 0) return

      this.currentIndex += 1

      if (this.currentIndex > this.slideCount) {
        this.snapToStart()
        return
      }

      if (this.currentIndex === this.slideCount && this.prefersReducedMotion()) {
        this.snapToStart()
        return
      }

      if (this.currentIndex === this.slideCount) {
        this.scheduleSnapFallback()
      }
    },

    prefersReducedMotion () {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },

    snapToStart () {
      this.clearSnapFallback()
      this.isSnapping = true
      this.noTransition = true
      this.currentIndex = 0

      this.$nextTick(() => {
        const track = this.$refs.track
        if (track) {
          void track.offsetHeight
        }
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.noTransition = false
            this.isSnapping = false
          })
        })
      })
    },

    onTrackTransitionEnd (event) {
      if (event.propertyName !== 'transform') return
      if (event.target !== this.$refs.track) return
      if (this.currentIndex !== this.slideCount) return

      this.snapToStart()
    }
  }
}

</script>
